import { useCallback, useRef } from 'react';
import { raiseIntent } from '@finos/fdc3';
import { Box } from '@mui/material';
import { EventOrigin } from '#features/analytics/eventOrigin';
import { trackEvent } from '#features/analytics/tracking';
import { showErrorOverlay } from '#features/error/state';
import { IntentName } from '#features/fdc3/model';
import { fdc3IntentUserEventLookup } from '#features/fdc3/trackingEvents';
import { useIntentListener } from '#features/fdc3/useIntentListener';
import { SearchResult } from '../../types';
import { Search, SearchProps } from './Search';

type WebSmartBarProps = Pick<SearchProps, 'closeOnClickAway' | 'sx'>;

export const WebSmartBar = (props: WebSmartBarProps) => {
  const smartBarRef = useRef<HTMLDivElement>(null);

  const focusInput = useCallback(() => {
    const searchInput = smartBarRef.current?.querySelector('input');
    if (searchInput) {
      searchInput.focus();
      searchInput.select();
    }
  }, []);

  useIntentListener(IntentName.FocusSmartBar, focusInput);

  const handleClick = async (result: SearchResult) => {
    if (result.action) {
      try {
        const event =
          fdc3IntentUserEventLookup[result.action.intent as IntentName];
        if (event)
          trackEvent(event, EventOrigin.ToolBar, result.action.context);
        await raiseIntent(result.action.intent, result.action.context);
      } catch {
        showErrorOverlay({
          title: 'Error',
          message: 'Failed to open',
          description: `There was a problem opening ${result.action.context?.id?.title ?? 'the window'}. Please try again or contact support.`,
          variant: 'error',
          closeButtonText: 'Close',
          showCloseButton: true,
          showContactSupportButton: true,
        });
      }
    }
  };

  return (
    <Box ref={smartBarRef}>
      <Search onClick={handleClick} {...props} />
    </Box>
  );
};
